import { chain, flatten, groupBy, mapValues } from "lodash";
import { applySnapshot, getParent, Instance, types } from "mobx-state-tree";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import { config } from "@fidget/common/config/config";
import { DashboardView } from "../../containers/dashboard/views/DashboardView";
import { currentUserStoreGetter, uiStoreGetter } from "../utils";
import { Report } from "./Report";
import { ReportsScope } from "./ReportsScope";

const ReportsTab = types
  .model("ReportsTab", {
    tabId: types.identifierNumber, // inline with DashboardView value for this tab
    reports: types.array(Report)
  })
  .views((self) => ({
    get label(): string {
      switch (self.tabId) {
        case DashboardView.REPORTS_EFFICIENCY:
          return intl.formatMessage("reports.efficiency");
        case DashboardView.REPORTS_SERVICE_LEVEL_TAG_GROUPS:
          return intl.formatMessage("reports.serviceLevelTagGroups");
        case DashboardView.REPORTS_SERVICE_LEVEL_TAGS:
          return intl.formatMessage("reports.serviceLevelTags");
        case DashboardView.REPORTS_BACK_LINE_TAGS:
          return intl.formatMessage("reports.backLineTags");
        case DashboardView.REPORTS_ADVISER_GROUPS:
          return intl.formatMessage("reports.adviserGroups");
        case DashboardView.REPORTS_ADVISER_TAG_GROUPS:
          return intl.formatMessage("reports.tagGroups");
        case DashboardView.REPORTS_OTHER:
          return intl.formatMessage("reports.other");
        default:
          return "";
      }
    },
    get dashboardView(): DashboardView {
      return self.tabId as DashboardView;
    },
    get scope(): ReportsScope {
      return (getParent(getParent(self)) as IReportTabsGroup).reportsScope;
    },
    createReportsUrl() {
      self.reports.forEach((report) => report.createUrl());
    }
  }));
export interface IReportsTab extends Instance<typeof ReportsTab> {}

const ReportTabsGroup = types
  .model("ScopedTabs", {
    scope: types.identifier,
    tabs: types.map(ReportsTab)
  })
  .views((self) => ({
    get label(): string {
      const scope = self.scope as ReportsScope;
      switch (scope) {
        case ReportsScope.Yours:
          return intl.formatMessage("reports.tab.yours");
        case ReportsScope.Groups:
          return intl.formatMessage("reports.tab.groups");
        case ReportsScope.Conversations:
          return intl.formatMessage("reports.tab.conversations");
        case ReportsScope.Advisers:
          return intl.formatMessage("reports.tab.advisers");
        case ReportsScope.Org:
          return intl.formatMessage("reports.tab.org");
        default:
          assertUnreachable(scope);
          return "";
      }
    },
    get reportsTabs(): IReportsTab[] {
      return [...self.tabs.values()];
    },
    get reportsScope(): ReportsScope {
      return self.scope as ReportsScope;
    },
    createReportsUrl() {
      self.tabs.forEach((tab) => tab.createReportsUrl());
    }
  }));
export interface IReportTabsGroup extends Instance<typeof ReportTabsGroup> {}

export const ReportsStore = types
  .model("ReportsStore", {
    tabsGroups: types.map(ReportTabsGroup)
  })
  .views(currentUserStoreGetter)
  .views(uiStoreGetter)
  .views((self) => ({
    refreshRaports() {
      self.tabsGroups.forEach((raportTabsGroup) => raportTabsGroup.createReportsUrl());
    }
  }))
  .views((self) => ({
    get yoursTabsGroup(): IReportTabsGroup | undefined {
      return self.tabsGroups.get(ReportsScope.Yours);
    },
    get groupTabsGroup(): IReportTabsGroup | undefined {
      return self.tabsGroups.get(ReportsScope.Groups);
    },
    get conversationsTabsGroup(): IReportTabsGroup | undefined {
      return self.tabsGroups.get(ReportsScope.Conversations);
    },
    get advisersTabsGroup(): IReportTabsGroup | undefined {
      return self.tabsGroups.get(ReportsScope.Advisers);
    },
    get orgTabsGroup(): IReportTabsGroup | undefined {
      return self.tabsGroups.get(ReportsScope.Org);
    }
  }))
  .views((self) => ({
    get landingTab(): { scope: ReportsScope; view: DashboardView } | undefined {
      const [firstYourReportTab] = self.yoursTabsGroup?.reportsTabs || [];
      if (firstYourReportTab) {
        return { scope: ReportsScope.Yours, view: firstYourReportTab.dashboardView };
      }

      const [firstGroupReportTab] = self.groupTabsGroup?.reportsTabs || [];
      if (firstGroupReportTab) {
        return { scope: ReportsScope.Groups, view: firstGroupReportTab.dashboardView };
      }

      const [firstOrgReportTab] = self.orgTabsGroup?.reportsTabs || [];
      if (firstOrgReportTab) {
        return { scope: ReportsScope.Org, view: firstOrgReportTab.dashboardView };
      }

      return;
    },
    get allTabs(): IReportsTab[] {
      return flatten(Array.from(self.tabsGroups.values(), (rtg) => [...rtg.tabs.values()]));
    }
  }))
  .actions((self) => ({
    prepare() {
      const {
        services: { metabase }
      } = config;
      const makeReportSnapshot = (id: string) => ({ id });
      const makeReportSnapshots = (ids: string[]) => ids.map(makeReportSnapshot);

      const tabsGroups = chain([
        {
          scope: ReportsScope.Yours,
          tabId: DashboardView.REPORTS_EFFICIENCY,
          reports: makeReportSnapshots(metabase.yours.efficiencyIds),
        },
      ].concat(self.currentUserStore.profile!.groups.length === 0 ? [] : [
        {
          scope: ReportsScope.Groups,
          tabId: DashboardView.REPORTS_EFFICIENCY,
          reports: makeReportSnapshots(metabase.groups.efficiencyIds),
        },
      ]).concat([
        {
          scope: ReportsScope.Conversations,
          tabId: DashboardView.REPORTS_SERVICE_LEVEL_TAG_GROUPS,
          reports: makeReportSnapshots(metabase.conversations.serviceLevelTagGroupsIds),
        },
        {
          scope: ReportsScope.Conversations,
          tabId: DashboardView.REPORTS_SERVICE_LEVEL_TAGS,
          reports: makeReportSnapshots(metabase.conversations.serviceLevelTagsIds),
        },
        {
          scope: ReportsScope.Conversations,
          tabId: DashboardView.REPORTS_BACK_LINE_TAGS,
          reports: makeReportSnapshots(metabase.conversations.backLineTagsIds),
        },
        {
          scope: ReportsScope.Advisers,
          tabId: DashboardView.REPORTS_ADVISER_GROUPS,
          reports: makeReportSnapshots(metabase.advisers.groupsIds),
        },
        {
          scope: ReportsScope.Advisers,
          tabId: DashboardView.REPORTS_ADVISER_TAG_GROUPS,
          reports: makeReportSnapshots(metabase.advisers.tagGroupsIds),
        },
        {
          scope: ReportsScope.Org,
          tabId: DashboardView.REPORTS_OTHER,
          reports: makeReportSnapshots(metabase.org.otherIds),
        },
        {
          scope: ReportsScope.Org,
          tabId: DashboardView.REPORTS_EFFICIENCY,
          reports: makeReportSnapshots(metabase.org.efficiencyIds),
        },
      ])
        .filter((v) => !!v.reports.length)
        .filter((v) => self.uiStore.canDisplayReports(v.scope, v.tabId))
      )
        .groupBy((v) => v.scope)
        .mapValues((l) => ({scope: l[0].scope, tabs: mapValues(groupBy(l, (v) => v.tabId), (_) => _[0])}))
        .value();

      applySnapshot(self, {
        tabsGroups
      });
    }
  }));

export interface IReportsStore extends Instance<typeof ReportsStore> {}
